import { CallEmsApi, iEmsApiResponseBody, networkFailure } from "../Logic/TenantAPI";

/*
 * DTOs for the Core tenant and organization configuration endpoints (ADO 10076).
 * Contract: chouette-ems-main docs/handoffs/2026-10-22-admin-ui-tenant-org-config.md.
 *
 * Every property is OPTIONAL. The API omits null properties from its JSON, so
 * a missing property means null. `stored` is the database row (absent: no
 * row). `effective` is what this API host uses after overlaying the row on its
 * appsettings (absent: this host has not applied a database-only code).
 *
 * These routes (Core/Organizations, Core/Tenants, Core/Connections) ignore the tenant claim in the token. Any
 * sys-admin token, which the STS issues only with ?elevate=true, reads and writes across all tenants.
 */

export const MAIL_PROVIDERS = ["Office365", "Basic", "Gmail", "Graph"] as const;
export type MailProvider = (typeof MAIL_PROVIDERS)[number];

export const THEMES = ["Default", "OldMutual"] as const;

export interface MailServerSettings {
    mailProvider?: MailProvider;
    server?: string;
    port?: number;
    username?: string;
    msalTenantId?: string;
    msalClientId?: string;
}

export interface OutboundMailServerSettings extends MailServerSettings {
    /** "none" means an unauthenticated relay. Absent means authenticate. Basic and Gmail only. */
    authenticationMethods?: string;
}

export interface SecretState {
    isSet?: boolean;
    /** Stars plus the last 2 characters. Absent while `isSet` means the host could not decrypt it. */
    masked?: string;
}

export const SECRET_NAMES = [
    "inboundPassword",
    "inboundMsalClientSecret",
    "outboundPassword",
    "outboundMsalClientSecret",
] as const;
export type SecretName = (typeof SECRET_NAMES)[number];

export type OrganizationSecrets = Partial<Record<SecretName, SecretState>>;

export interface OrganizationSettings {
    name?: string;
    enableCloudMailProcessing?: boolean;
    autoResponseMaxPerWindow?: number;
    autoResponseWindowMinutes?: number;
    autoResponseBodySizeToleranceBytes?: number;
    inbound?: MailServerSettings;
    outbound?: OutboundMailServerSettings;
    secrets?: OrganizationSecrets;
    /** UTC, serialised without a `Z`. Parse with parseUtcDate. Stored layer only. */
    modifiedAt?: string;
    modifiedBy?: string;
}

export interface OrganizationItem {
    code: string;
    stored?: OrganizationSettings;
    effective?: OrganizationSettings;
}

/**
 * The PUT body. It REPLACES the stored row: a null or omitted field clears
 * that override. A null secret keeps the stored secret; it is the one
 * exception, because secrets are cleared with DeleteOrganizationSecret.
 */
export interface OrganizationRequest {
    name: string | null;
    enableCloudMailProcessing: boolean | null;
    autoResponseMaxPerWindow: number | null;
    autoResponseWindowMinutes: number | null;
    autoResponseBodySizeToleranceBytes: number | null;
    inbound: { [K in keyof MailServerSettings]-?: MailServerSettings[K] | null };
    outbound: { [K in keyof OutboundMailServerSettings]-?: OutboundMailServerSettings[K] | null };
    inboundPassword: string | null;
    inboundMsalClientSecret: string | null;
    outboundPassword: string | null;
    outboundMsalClientSecret: string | null;
}

export interface TenantSettings {
    name?: string;
    disabled?: boolean;
    organization?: string;
    enableCloudMailProcessing?: boolean;
    autoResponseMaxPerWindow?: number;
    autoResponseWindowMinutes?: number;
    autoResponseBodySizeToleranceBytes?: number;
    theme?: string;
    s3CachePrefix?: string;
    sqsQueueUrl?: string;
    sqsRegion?: string;
    connection?: string;
    database?: string;
    useRdsProxy?: boolean;
    modifiedAt?: string;
    modifiedBy?: string;
}

export interface TenantItem {
    code: string;
    stored?: TenantSettings;
    effective?: TenantSettings;
}

export type TenantRequest = {
    [K in Exclude<keyof TenantSettings, "modifiedAt" | "modifiedBy">]-?: TenantSettings[K] | null;
};

/** The route name of each secret, for DELETE .../Secrets/{secret}. */
const SECRET_ROUTE: Record<SecretName, string> = {
    inboundPassword: "InboundPassword",
    inboundMsalClientSecret: "InboundMsalClientSecret",
    outboundPassword: "OutboundPassword",
    outboundMsalClientSecret: "OutboundMsalClientSecret",
};

async function call<T>(
    token: string,
    method: "GET" | "PUT" | "DELETE",
    path: string,
    body?: unknown,
): Promise<iEmsApiResponseBody<T>> {
    try {
        return await CallEmsApi<T>(token, method, path, body);
    } catch {
        return networkFailure<T>();
    }
}

const org = (code: string) => `Core/Organizations/${encodeURIComponent(code)}`;
const tenant = (code: string) => `Core/Tenants/${encodeURIComponent(code)}`;

export function GetOrganizations(token: string) {
    return call<OrganizationItem[]>(token, "GET", "Core/Organizations");
}

export function GetOrganization(token: string, code: string) {
    return call<OrganizationItem>(token, "GET", org(code));
}

export function PutOrganization(token: string, code: string, body: OrganizationRequest) {
    return call<OrganizationItem>(token, "PUT", org(code), body);
}

/** 409 when a tenant still links to it and appsettings does not define it. See `conflict.detail`. */
export function DeleteOrganization(token: string, code: string) {
    return call<void>(token, "DELETE", org(code));
}

export function DeleteOrganizationSecret(token: string, code: string, secret: SecretName) {
    return call<void>(token, "DELETE", `${org(code)}/Secrets/${SECRET_ROUTE[secret]}`);
}

export function GetTenants(token: string) {
    return call<TenantItem[]>(token, "GET", "Core/Tenants");
}

export function GetTenant(token: string, code: string) {
    return call<TenantItem>(token, "GET", tenant(code));
}

export function PutTenant(token: string, code: string, body: TenantRequest) {
    return call<TenantItem>(token, "PUT", tenant(code), body);
}

export function DeleteTenant(token: string, code: string) {
    return call<void>(token, "DELETE", tenant(code));
}

/** The configured connection-string names, sorted case-insensitively. The values are never returned. */
export function GetConnections(token: string) {
    return call<string[]>(token, "GET", "Core/Connections");
}
